import React from 'react';

type Config = {
  viewportRef: React.RefObject<HTMLElement>;
  scrollerRef: React.RefObject<HTMLElement>;
  /** When false, animation is paused and position resets to start. */
  active: boolean;
  /** Dependencies that should trigger re-measure (e.g., language, content). */
  deps?: React.DependencyList;
  /** Pause at each edge in milliseconds. Default: 3000 */
  pauseMs?: number;
  /** Horizontal speed in pixels per second. Default: 40 */
  speedPxPerSec?: number;
};

const useMarqueeOverflow = (config: Config) => {
  const { viewportRef, scrollerRef } = config;
  const timersRef = React.useRef<{ t1?: number; t2?: number; t3?: number; t4?: number }>({});
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [scrollDistance, setScrollDistance] = React.useState(0);

  const measure = React.useCallback(() => {
    const viewport = viewportRef.current;
    const scroller = scrollerRef.current;
    if (!viewport || !scroller) {
      setIsOverflowing(false);
      setScrollDistance(0);
      return;
    }
    const vw = Math.round(viewport.clientWidth);
    const sw = Math.round(scroller.scrollWidth);
    const dist = Math.max(0, sw - vw);
    setIsOverflowing(dist > 0);
    setScrollDistance(dist);
  }, [viewportRef, scrollerRef]);

  React.useEffect(() => {
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, config.deps ?? []);

  React.useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  React.useEffect(() => {
    const scroller = scrollerRef.current;
    const clearTimers = () => {
      const { t1, t2, t3, t4 } = timersRef.current;
      if (t1) {
        window.clearTimeout(t1);
      }
      if (t2) {
        window.clearTimeout(t2);
      }
      if (t3) {
        window.clearTimeout(t3);
      }
      if (t4) {
        window.clearTimeout(t4);
      }
      timersRef.current = {};
    };

    if (!scroller) {
      clearTimers();
      return;
    }

    if (!isOverflowing || !config.active || scrollDistance <= 0) {
      clearTimers();
      scroller.style.transition = 'transform 0ms linear';
      scroller.style.transform = 'translateX(0)';
      return;
    }

    const pauseMs = config.pauseMs ?? 3000;
    const speedPxPerSec = config.speedPxPerSec ?? 40;
    const travelMs = Math.max(1, Math.round((scrollDistance / speedPxPerSec) * 1000));

    const cycle = () => {
      // Pause at start
      scroller.style.transition = 'transform 0ms linear';
      scroller.style.transform = 'translateX(0)';
      timersRef.current.t1 = window.setTimeout(() => {
        // Move to end
        scroller.style.transition = `transform ${travelMs}ms linear`;
        scroller.style.transform = `translateX(-${scrollDistance}px)`;
        timersRef.current.t2 = window.setTimeout(() => {
          // Pause at end
          scroller.style.transition = 'transform 0ms linear';
          scroller.style.transform = `translateX(-${scrollDistance}px)`;
          timersRef.current.t3 = window.setTimeout(() => {
            // Move back to start
            scroller.style.transition = `transform ${travelMs}ms linear`;
            scroller.style.transform = 'translateX(0)';
            timersRef.current.t4 = window.setTimeout(() => {
              // Pause at start then repeat
              cycle();
            }, pauseMs);
          }, pauseMs);
        }, travelMs);
      }, pauseMs);
    };

    cycle();
    return clearTimers;
  }, [
    config.active,
    config.pauseMs,
    config.speedPxPerSec,
    isOverflowing,
    scrollDistance,
    scrollerRef,
  ]);

  return { isOverflowing, scrollDistance } as const;
};

export default useMarqueeOverflow;
